import React, { useMemo } from 'react';
import { HashLink as Link } from 'react-router-hash-link';
import { NamedLink } from '../components';
import { scrollWithOffset } from '../util/listing';

/**
 * Custom hook to prepare data for PackageHeader component
 * Works for both Listing and Package pages
 * @param {Object} params
 * @param {Object} params.data - The formatted data object (from useListingData or usePackageData)
 *   Should contain: title, nearestTown, acreage, gmu (optional), listingTitle (optional)
 * @param {Object} params.listingParams - Optional listing route params for creating subheader link
 * @param {Array} params.reviews - Array of reviews
 * @param {Object} params.reviewsMeta - Reviews metadata with average rating
 * @param {boolean} params.isFetchingReviews - Whether reviews are being fetched
 * @param {Object} params.intl - The intl object for internationalization
 * @returns {Object} - Object containing header, subheader, and features for PackageHeader
 */
const useHeader = ({
  data,
  listingParams,
  reviews = [],
  reviewsMeta,
  isFetchingReviews = false,
  intl,
}) => {
  const { title, gmu, listingTitle, acreage, nearestTown } = data || {};

  const subheader = useMemo(() => {
    if (!listingTitle || !listingParams) return null;

    const atText = intl.formatMessage({ id: 'PackagePage.atTitle' });
    return (
      <>
        {atText}{' '}
        <NamedLink
          name="ListingPage"
          params={listingParams}
          style={{ textDecoration: 'underline' }}
        >
          {listingTitle}
        </NamedLink>
      </>
    );
  }, [listingTitle, listingParams, intl]);

  const features = useMemo(() => {
    const featuresList = [];

    // Location feature
    if (nearestTown) {
      featuresList.push({
        iconVariant: 'LocationDotSolid',
        label: nearestTown,
      });
    }

    // Acres and GMU feature
    if (acreage || gmu) {
      const parts = [];
      if (acreage) {
        parts.push(intl.formatMessage({ id: 'ListingPage.keyInfoAcreage' }, { acres: acreage }));
      }
      if (gmu) {
        parts.push(`GMU: ${gmu}`);
      }
      featuresList.push({
        iconVariant: 'GridSolid',
        label: parts.join(' • '),
      });
    }

    // Reviews feature
    const hasReviewsData = reviews !== null;

    if (isFetchingReviews || !hasReviewsData) {
      // Show loading state while fetching or if reviews haven't been loaded yet
      featuresList.push({
        iconVariant: 'StarSolid',
        iconColor: 'var(--icon-warning)',
        label: '',
        isLoading: true,
      });
    } else {
      const fixedRating = reviewsMeta?.customerAverageRating || 0;
      const totalReviews = reviews?.length || 0;
      const isReviewed = totalReviews > 0;

      if (isReviewed) {
        featuresList.push({
          iconVariant: 'StarSolid',
          iconColor: 'var(--icon-warning)',
          label: (
            <span>
              {fixedRating} •{' '}
              <Link
                to="#listingReviews"
                style={{ textDecoration: 'underline' }}
                scroll={scrollWithOffset}
                onClick={e => e.preventDefault()}
              >
                {intl.formatMessage({ id: 'PackagePage.reviews' }, { count: totalReviews })}
              </Link>
            </span>
          ),
        });
      } else {
        featuresList.push({
          iconVariant: 'StarSolid',
          iconColor: 'var(--icon-primary)',
          label: intl.formatMessage({ id: 'PackagePage.noReviews' }),
        });
      }
    }

    return featuresList;
  }, [nearestTown, acreage, gmu, reviews, reviewsMeta, isFetchingReviews, intl]);

  return {
    header: title,
    subheader,
    features,
  };
};

export default useHeader;
